import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Shuffle, Trophy, RotateCcw } from 'lucide-react';
import { Word } from '@/data/words';

interface MatchingGameProps {
  words: Word[];
  onBack: () => void;
}

export function MatchingGame({ words, onBack }: MatchingGameProps) {
  const [gameWords] = useState(() => words.slice(0, 6)); // Use first 6 words
  const [selectedWord, setSelectedWord] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [matches, setMatches] = useState<Set<string>>(new Set());
  const [score, setScore] = useState(0);
  const [gameComplete, setGameComplete] = useState(false);

  const shuffledWords = [...gameWords].sort(() => Math.random() - 0.5);
  const shuffledImages = [...gameWords].sort(() => Math.random() - 0.5);

  const handleWordClick = (wordId: string) => {
    if (matches.has(wordId)) return;
    
    setSelectedWord(wordId);
    
    if (selectedImage && selectedImage === wordId) {
      // Match found!
      setMatches(prev => new Set([...prev, wordId]));
      setScore(prev => prev + 10);
      setSelectedWord(null);
      setSelectedImage(null);
      
      if (matches.size + 1 === gameWords.length) {
        setGameComplete(true);
      }
    } else if (selectedImage) {
      // Wrong match
      setTimeout(() => {
        setSelectedWord(null);
        setSelectedImage(null);
      }, 1000);
    }
  };

  const handleImageClick = (wordId: string) => {
    if (matches.has(wordId)) return;
    
    setSelectedImage(wordId);
    
    if (selectedWord && selectedWord === wordId) {
      // Match found!
      setMatches(prev => new Set([...prev, wordId]));
      setScore(prev => prev + 10);
      setSelectedWord(null);
      setSelectedImage(null);
      
      if (matches.size + 1 === gameWords.length) {
        setGameComplete(true);
      }
    } else if (selectedWord) {
      // Wrong match
      setTimeout(() => {
        setSelectedWord(null);
        setSelectedImage(null);
      }, 1000);
    }
  };

  const resetGame = () => {
    setSelectedWord(null);
    setSelectedImage(null);
    setMatches(new Set());
    setScore(0);
    setGameComplete(false);
  };

  if (gameComplete) {
    return (
      <div className="flex flex-col items-center space-y-6 p-8">
        <div className="text-center">
          <Trophy className="w-20 h-20 text-yellow-500 mx-auto mb-4 animate-bounce" />
          <h2 className="text-4xl font-bold rainbow-text mb-2">Congratulations!</h2>
          <p className="text-xl text-gray-600 mb-4">You matched all the words!</p>
          <p className="text-2xl font-bold text-primary">Final Score: {score} points</p>
        </div>
        
        <div className="flex space-x-4">
          <Button onClick={resetGame} className="btn-fun" size="lg">
            <RotateCcw className="w-5 h-5 mr-2" />
            Play Again
          </Button>
          <Button onClick={onBack} variant="outline" size="lg">
            Back to Categories
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <Button onClick={onBack} variant="outline">
          ← Back
        </Button>
        <div className="text-center">
          <h2 className="text-2xl font-bold rainbow-text">Matching Game</h2>
          <p className="text-lg text-primary font-semibold">Score: {score}</p>
        </div>
        <Button onClick={resetGame} variant="outline">
          <Shuffle className="w-4 h-4 mr-2" />
          Reset
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-8 max-w-4xl mx-auto">
        {/* Words column */}
        <div className="space-y-3">
          <h3 className="text-xl font-bold text-center mb-4 text-primary">Words</h3>
          {shuffledWords.map((word) => (
            <Card
              key={`word-${word.id}`}
              className={`cursor-pointer transition-all duration-300 ${
                matches.has(word.id)
                  ? 'bg-green-100 border-green-400'
                  : selectedWord === word.id
                  ? 'bg-blue-100 border-blue-400 scale-105'
                  : 'hover:scale-105 hover:shadow-lg'
              }`}
              onClick={() => handleWordClick(word.id)}
            >
              <CardContent className="p-4 text-center">
                <span className="text-2xl font-bold">{word.word}</span>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Images column */}
        <div className="space-y-3">
          <h3 className="text-xl font-bold text-center mb-4 text-accent">Images</h3>
          {shuffledImages.map((word) => (
            <Card
              key={`image-${word.id}`}
              className={`cursor-pointer transition-all duration-300 ${
                matches.has(word.id)
                  ? 'bg-green-100 border-green-400'
                  : selectedImage === word.id
                  ? 'bg-blue-100 border-blue-400 scale-105'
                  : 'hover:scale-105 hover:shadow-lg'
              }`}
              onClick={() => handleImageClick(word.id)}
            >
              <CardContent className="p-2">
                <img
                  src={word.image}
                  alt={word.word}
                  className="w-full h-20 object-cover rounded"
                />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Instructions */}
      <div className="text-center mt-8 p-4 bg-blue-50 rounded-lg">
        <p className="text-gray-600">
          Match the words with their pictures! Click on a word, then click on its matching image.
        </p>
      </div>
    </div>
  );
}